/**
 * AttachmentPreviewBar - Horizontal scrollable bar showing attached files.
 *
 * Features:
 * - Horizontal scroll for many files
 * - Shows FilePreviewChip for each attachment
 * - Overall progress indicator when uploading
 * - Collapse to count on mobile (optional)
 */

import React from 'react';
import { Box, LinearProgress, Typography, Collapse } from '@mui/material';

import { FileAttachment } from '../../types/upload';
import FilePreviewChip from './FilePreviewChip';

export interface AttachmentPreviewBarProps {
  /** List of attachments to display */
  attachments: FileAttachment[];
  /** Callback when an attachment is removed */
  onRemove: (id: string) => void;
  /** Whether interactions are disabled */
  disabled?: boolean;
  /** Whether any files are currently uploading */
  isUploading?: boolean;
  /** Overall upload progress (0-100) */
  uploadProgress?: number;
}

export const AttachmentPreviewBar: React.FC<AttachmentPreviewBarProps> = ({
  attachments,
  onRemove,
  disabled = false,
  isUploading = false,
  uploadProgress = 0,
}) => {
  if (attachments.length === 0) {
    return null;
  }

  return (
    <Collapse in={attachments.length > 0}>
      <Box
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          backgroundColor: 'grey.50',
          p: 1,
        }}
      >
        {/* Header with count and progress */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 0.5,
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontWeight: 500 }}
          >
            {attachments.length} file{attachments.length !== 1 ? 's' : ''}{' '}
            attached
          </Typography>
          {isUploading && (
            <Typography variant="caption" color="primary">
              Uploading... {uploadProgress}%
            </Typography>
          )}
        </Box>

        {/* Progress bar when uploading */}
        {isUploading && (
          <LinearProgress
            variant="determinate"
            value={uploadProgress}
            sx={{ mb: 1, height: 2, borderRadius: 1 }}
          />
        )}

        {/* Scrollable chips container */}
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            overflowX: 'auto',
            pb: 0.5,
            // Custom scrollbar styling
            '&::-webkit-scrollbar': {
              height: 4,
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'grey.200',
              borderRadius: 2,
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'grey.400',
              borderRadius: 2,
              '&:hover': {
                backgroundColor: 'grey.500',
              },
            },
            // Firefox scrollbar
            scrollbarWidth: 'thin',
            scrollbarColor: '#9e9e9e #e0e0e0',
          }}
        >
          {attachments.map((attachment) => (
            <FilePreviewChip
              key={attachment.id}
              attachment={attachment}
              onRemove={onRemove}
              disabled={disabled}
            />
          ))}
        </Box>
      </Box>
    </Collapse>
  );
};

export default AttachmentPreviewBar;
